import React, { useMemo, useState } from 'react';
import { universities } from '../data/mock';

// --- City coordinates (lat, lon) for cities with partner universities ---------
// Only cities that actually appear in /app/frontend/src/data/universities.js are listed.
const CITY_COORDS = {
  Moscow: [55.75, 37.62], 'Saint Petersburg': [59.94, 30.31], Kazan: [55.79, 49.12],
  Novosibirsk: [55.03, 82.92], Yekaterinburg: [56.83, 60.60], 'Nizhny Novgorod': [56.32, 44.00],
  Samara: [53.25, 50.20], 'Rostov-on-Don': [47.24, 39.71], Ufa: [54.74, 55.97],
  Krasnoyarsk: [56.01, 92.87], Perm: [58.01, 56.23], Volgograd: [48.71, 44.51],
  Saratov: [51.53, 46.03], Krasnodar: [45.03, 38.98], Izhevsk: [56.85, 53.20],
  Ulyanovsk: [54.32, 48.39], Barnaul: [53.35, 83.78], Vladivostok: [43.13, 131.90],
  Yaroslavl: [57.62, 39.87], Irkutsk: [52.29, 104.30], Makhachkala: [42.98, 47.51],
  Orenburg: [51.78, 55.10], Tomsk: [56.50, 84.98], Kemerovo: [55.35, 86.09],
  Ryazan: [54.63, 39.72], Astrakhan: [46.35, 48.03], Kirov: [58.60, 49.66],
  Cheboksary: [56.14, 47.24], Tula: [54.20, 37.62], Kaluga: [54.51, 36.28],
  Belgorod: [50.60, 36.58], Vladikavkaz: [43.02, 44.68], Kursk: [51.73, 36.19],
  Stavropol: [45.05, 41.98], 'Ulan-Ude': [51.83, 107.61], Tver: [56.86, 35.90],
  Tyumen: [57.15, 65.53], Chelyabinsk: [55.16, 61.40], Omsk: [54.99, 73.37],
  Saransk: [54.19, 45.19], Yakutsk: [62.03, 129.73], Blagoveshchensk: [50.29, 127.53],
  Petrozavodsk: [61.79, 34.35], Arkhangelsk: [64.55, 40.53], Murmansk: [68.97, 33.08],
  Smolensk: [54.78, 32.05], Simferopol: [44.95, 34.10], Nalchik: [43.48, 43.60],
  Elista: [46.31, 44.27], Maykop: [44.61, 40.11], Syktyvkar: [61.67, 50.83],
  Abakan: [53.72, 91.44], Pskov: [57.82, 28.34], Tambov: [52.73, 41.44],
  'Yoshkar-Ola': [56.63, 47.90], Orel: [52.97, 36.07], Yelets: [52.62, 38.51],
};

// SVG viewport dimensions & projection ranges
const W = 1000;
const H = 460;
const LON_MIN = 18;
const LON_MAX = 192;
const LAT_MAX = 78; // top of viewbox
const LAT_MIN = 40; // bottom of viewbox

function project([lat, lon]) {
  const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * W;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * H;
  return [x, y];
}

// Stylised, approximate outline of Russia (not cartographically accurate — hero-style silhouette).
const RUSSIA_PATH = 'M30,180 L60,140 L110,120 L160,100 L210,90 L260,80 L310,75 L360,80 L400,70 L440,80 L490,90 L540,95 L590,110 L620,130 L660,140 L700,145 L740,155 L780,170 L820,180 L870,200 L900,225 L925,255 L950,290 L965,325 L960,355 L940,375 L900,385 L860,378 L820,368 L780,360 L750,350 L720,345 L690,352 L660,360 L630,365 L600,370 L570,375 L540,378 L510,380 L480,380 L450,378 L420,375 L390,368 L360,362 L330,360 L300,362 L270,368 L240,375 L210,380 L180,385 L150,382 L120,375 L90,362 L60,340 L45,310 L38,275 L35,240 L30,205 Z';

// Aggregate universities by city
function buildCityData() {
  const groups = new Map();
  universities.forEach((u) => {
    if (!CITY_COORDS[u.city]) return;
    if (!groups.has(u.city)) groups.set(u.city, []);
    groups.get(u.city).push(u);
  });
  return Array.from(groups.entries()).map(([city, unis]) => ({
    city,
    coords: CITY_COORDS[city],
    unis,
    count: unis.length,
  }));
}

export default function RussiaMap() {
  const cities = useMemo(buildCityData, []);
  const moscow = cities.find((c) => c.city === 'Moscow') || {
    city: 'Moscow', coords: CITY_COORDS.Moscow, unis: [], count: 0,
  };
  const [Mx, My] = project(moscow.coords);
  const [hovered, setHovered] = useState(null);

  return (
    <div className="relative" data-testid="russia-map">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2 text-xs md:text-sm">
          <span className="inline-block w-3 h-3 rounded-full bg-brand ring-2 ring-white shadow-md shadow-brand/40" />
          <span className="text-muted-foreground">University City</span>
        </div>
        <div className="flex items-center gap-2 text-xs md:text-sm">
          <span className="inline-block w-4 h-4 rounded-full bg-white ring-4 ring-brand shadow-md" />
          <span className="text-muted-foreground">Moscow — Hub</span>
        </div>
        <div className="flex items-center gap-2 text-xs md:text-sm">
          <span className="inline-block h-[2px] w-8 bg-brand/60" style={{ boxShadow: '0 0 6px rgba(220,38,38,0.6)' }} />
          <span className="text-muted-foreground">Connection to Moscow</span>
        </div>
        <div className="ml-auto text-xs md:text-sm text-primary font-semibold">
          <span className="text-brand font-serif text-xl">{cities.length}</span> cities · <span className="text-brand font-serif text-xl">{cities.reduce((s, c) => s + c.count, 0)}</span> universities
        </div>
      </div>

      {/* 3D stage */}
      <div
        className="relative rounded-2xl overflow-hidden bg-blue-hero border-2 border-primary/30 shadow-2xl"
        style={{ perspective: '2400px' }}
      >
        {/* Grain overlay */}
        <div className="absolute inset-0 opacity-15 pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.12' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />

        {/* Header */}
        <div className="relative px-5 md:px-8 pt-6 pb-3 text-white z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand font-bold">Study Destinations</div>
          <div className="font-serif text-2xl md:text-3xl font-extrabold">Russia — University City Network</div>
        </div>

        {/* 3D tilted map */}
        <div
          className="relative mx-auto"
          style={{
            transformStyle: 'preserve-3d',
            transform: 'rotateX(22deg) scale(1.02)',
            transformOrigin: '50% 60%',
          }}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full h-auto block"
            role="img"
            aria-label="Map of Russia showing partner universities connected to Moscow"
          >
            <defs>
              <linearGradient id="landGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1E40AF" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#0B1E52" stopOpacity="0.85" />
              </linearGradient>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#DC2626" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#DC2626" stopOpacity="0.15" />
              </linearGradient>
              <radialGradient id="cityGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#DC2626" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#DC2626" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="moscowGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#DC2626" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#DC2626" stopOpacity="0" />
              </radialGradient>
              <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feOffset dx="0" dy="4" result="shift" />
                <feMerge>
                  <feMergeNode in="shift" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Faint lon/lat grid */}
            <g stroke="white" strokeOpacity="0.06" strokeWidth="0.6">
              {Array.from({ length: 9 }).map((_, i) => {
                const x = ((i + 1) / 10) * W;
                return <line key={`v${i}`} x1={x} y1="0" x2={x} y2={H} />;
              })}
              {Array.from({ length: 5 }).map((_, i) => {
                const y = ((i + 1) / 6) * H;
                return <line key={`h${i}`} x1="0" y1={y} x2={W} y2={y} />;
              })}
            </g>

            {/* Russia territory silhouette */}
            <path d={RUSSIA_PATH} fill="url(#landGrad)" stroke="#DC2626" strokeOpacity="0.55" strokeWidth="1.5" filter="url(#softShadow)" />
            <path d={RUSSIA_PATH} fill="none" stroke="white" strokeOpacity="0.12" strokeWidth="0.6" />

            {/* Connection lines Moscow → cities (curved quadratic bezier) */}
            <g>
              {cities.filter((c) => c.city !== 'Moscow').map((c) => {
                const [x, y] = project(c.coords);
                const midX = (Mx + x) / 2;
                const midY = Math.min(Mx, y) - 40 - Math.abs(x - Mx) * 0.08;
                const isHot = hovered && hovered.city === c.city;
                return (
                  <path
                    key={`ln-${c.city}`}
                    d={`M${Mx},${My} Q${midX},${midY} ${x},${y}`}
                    fill="none"
                    stroke="url(#lineGrad)"
                    strokeWidth={isHot ? 2 : 1}
                    strokeOpacity={isHot ? 1 : 0.7}
                    strokeDasharray={isHot ? '0' : '4 3'}
                    style={{ filter: isHot ? 'drop-shadow(0 0 4px rgba(220,38,38,0.9))' : 'none' }}
                  />
                );
              })}
            </g>

            {/* City dots (non-Moscow) */}
            <g>
              {cities.filter((c) => c.city !== 'Moscow').map((c) => {
                const [x, y] = project(c.coords);
                const r = 3 + Math.min(c.count - 1, 3);
                const isHot = hovered && hovered.city === c.city;
                return (
                  <g
                    key={c.city}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHovered({ ...c, x, y })}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <circle cx={x} cy={y} r={r + 6} fill="url(#cityGlow)" opacity={isHot ? 1 : 0.5} />
                    <circle cx={x} cy={y} r={r} fill="#DC2626" stroke="#fff" strokeWidth="1.2" />
                    {isHot && (
                      <circle cx={x} cy={y} r={r + 4} fill="none" stroke="#DC2626" strokeWidth="1" opacity="0.7" />
                    )}
                  </g>
                );
              })}
            </g>

            {/* Moscow — hub */}
            <g style={{ cursor: 'pointer' }}
               onMouseEnter={() => setHovered({ ...moscow, x: Mx, y: My })}
               onMouseLeave={() => setHovered(null)}>
              <circle cx={Mx} cy={My} r="24" fill="url(#moscowGlow)" />
              <circle cx={Mx} cy={My} r="12" fill="#fff" stroke="#DC2626" strokeWidth="3" />
              <circle cx={Mx} cy={My} r="4" fill="#DC2626" />
              <text x={Mx} y={My - 20} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">MOSCOW</text>
            </g>

            {/* Labels for a few key cities so map stays informative even without hover */}
            {['Saint Petersburg', 'Kazan', 'Novosibirsk', 'Yekaterinburg', 'Vladivostok', 'Krasnodar'].map((c) => {
              const meta = cities.find((x) => x.city === c);
              if (!meta) return null;
              const [x, y] = project(meta.coords);
              const above = c === 'Saint Petersburg' || c === 'Novosibirsk';
              return (
                <text key={`lbl-${c}`} x={x + 6} y={y + (above ? -8 : 14)} fontSize="9" fill="#fff" opacity="0.85" fontWeight="600">
                  {c}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Hover tooltip */}
        {hovered && (
          <div
            className="absolute z-20 pointer-events-none px-3 py-2 rounded-lg bg-white text-primary shadow-xl border border-brand/40"
            style={{
              left: `${(hovered.x / W) * 100}%`,
              top: `${(hovered.y / H) * 100 + 12}%`,
              transform: 'translate(-50%, 0)',
              minWidth: '160px',
            }}
          >
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">City</div>
            <div className="font-serif text-base font-bold leading-tight">{hovered.city}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {hovered.count} {hovered.count === 1 ? 'university' : 'universities'}
            </div>
          </div>
        )}

        {/* Bottom axis strip */}
        <div className="relative px-5 md:px-8 py-3 flex items-center justify-between text-[10px] text-white/60 uppercase tracking-widest">
          <span>West · Europe</span>
          <span>Volga</span>
          <span>Urals</span>
          <span>Siberia</span>
          <span>Far East · Pacific</span>
        </div>
      </div>

      {/* City grid list under map */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-[0.25em] text-brand font-bold">All Study Destinations</div>
        <h3 className="mt-2 font-serif text-xl md:text-2xl font-bold text-primary">Cities connected to Moscow via daily flights &amp; rail</h3>
        <div className="mt-5 flex flex-wrap gap-2">
          {cities
            .slice()
            .sort((a, b) => a.city.localeCompare(b.city))
            .map((c) => (
              <span
                key={`chip-${c.city}`}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  c.city === 'Moscow'
                    ? 'bg-brand text-white border-brand'
                    : 'bg-white text-primary border-border hover:border-brand/50 hover:text-brand'
                }`}
                data-testid={`city-chip-${c.city.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${c.city === 'Moscow' ? 'bg-white' : 'bg-brand'}`} />
                {c.city}
                <span className="opacity-70">·</span>
                <span className="opacity-80">{c.count}</span>
              </span>
            ))}
        </div>
      </div>
    </div>
  );
}
